import { useEffect, useRef } from 'react';

/* Shared motion hook: hero mouse/scroll parallax + reveal-on-scroll */
export function useLuxMotion() {
  const rootRef = useRef(null);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const cleanups = [];
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const hero = root.querySelector('.fx-hero');
    let mx = 0, my = 0, tmx = 0, tmy = 0, sy = 0, raf;
    const onMouse = (e) => {
      const r = hero.getBoundingClientRect();
      tmx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      tmy = ((e.clientY - r.top) / r.height - 0.5) * 2;
    };
    const onScroll = () => { sy = window.scrollY; };
    const loop = () => {
      mx += (tmx - mx) * 0.06; my += (tmy - my) * 0.06;
      hero.style.setProperty('--mx', mx.toFixed(3));
      hero.style.setProperty('--my', my.toFixed(3));
      hero.style.setProperty('--sy', sy.toFixed(1));
      raf = requestAnimationFrame(loop);
    };
    if (hero && !reduced) {
      hero.addEventListener('mousemove', onMouse);
      window.addEventListener('scroll', onScroll, { passive: true });
      raf = requestAnimationFrame(loop);
      cleanups.push(() => {
        hero.removeEventListener('mousemove', onMouse);
        window.removeEventListener('scroll', onScroll);
        cancelAnimationFrame(raf);
      });
    }

    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('on'); io.unobserve(en.target); } });
    }, { threshold: 0.12, rootMargin: '0px 0px -5% 0px' });
    root.querySelectorAll('.fx-reveal, .fx-pop').forEach((el) => io.observe(el));
    cleanups.push(() => io.disconnect());

    /* 3D tilt on cards */
    root.querySelectorAll('.fx-tilt').forEach((card) => {
      if (reduced) return;
      const mv = (e) => {
        const r = card.getBoundingClientRect();
        const dx = (e.clientX - r.left) / r.width - .5, dy = (e.clientY - r.top) / r.height - .5;
        card.style.transform = `perspective(1000px) rotateY(${dx * 6}deg) rotateX(${-dy * 6}deg) translateY(-6px)`;
      };
      const lv = () => { card.style.transform = ''; };
      card.addEventListener('mousemove', mv); card.addEventListener('mouseleave', lv);
      cleanups.push(() => { card.removeEventListener('mousemove', mv); card.removeEventListener('mouseleave', lv); });
    });

    /* "How it works" — 0→100 rail spanning exactly the steps list, fills on scroll */
    const method = root.querySelector('.fx-method');
    const mlist = root.querySelector('.fx-msteps');
    const railFill = root.querySelector('.fx-rail-fill');
    const railPct = root.querySelector('.fx-rail-pct');
    const updateRail = () => {
      if (!mlist || !railFill) return;
      const rect = mlist.getBoundingClientRect();
      const anchor = window.innerHeight * 0.6;
      let p = rect.height ? (anchor - rect.top) / rect.height : 0;
      p = Math.max(0, Math.min(1, p));
      railFill.style.transform = 'scaleY(' + p + ')';
      if (railPct) { railPct.textContent = Math.round(p * 100); railPct.style.top = (p * 100) + '%'; }
    };
    if (method && railFill) {
      window.addEventListener('scroll', updateRail, { passive: true });
      window.addEventListener('resize', updateRail);
      updateRail();
      cleanups.push(() => { window.removeEventListener('scroll', updateRail); window.removeEventListener('resize', updateRail); });
    }

    /* "How it works" — click-to-expand dropdown steps (recompute rail on expand) */
    const msteps = root.querySelectorAll('.fx-mstep');
    msteps.forEach((step) => {
      const head = step.querySelector('.fx-mstep-head'); if (!head) return;
      const toggle = () => {
        const open = step.classList.contains('open');
        msteps.forEach((s) => { s.classList.remove('open'); const b = s.querySelector('.fx-mstep-body'); if (b) b.style.maxHeight = '0px'; });
        if (!open) { step.classList.add('open'); const b = step.querySelector('.fx-mstep-body'); if (b) b.style.maxHeight = b.scrollHeight + 'px'; }
        requestAnimationFrame(updateRail);
        setTimeout(updateRail, 540);
      };
      head.addEventListener('click', toggle);
      cleanups.push(() => head.removeEventListener('click', toggle));
    });

    /* FAQ accordion */
    const faqItems = root.querySelectorAll('.fx-faq-item');
    faqItems.forEach((item) => {
      const q = item.querySelector('.fx-faq-q'); if (!q) return;
      const toggle = () => {
        const open = item.classList.contains('faq-open');
        faqItems.forEach((it) => { it.classList.remove('faq-open'); const a = it.querySelector('.fx-faq-a'); if (a) a.style.maxHeight = '0px'; });
        if (!open) { item.classList.add('faq-open'); const a = item.querySelector('.fx-faq-a'); if (a) a.style.maxHeight = a.scrollHeight + 'px'; }
      };
      q.addEventListener('click', toggle);
      cleanups.push(() => q.removeEventListener('click', toggle));
    });

    return () => cleanups.forEach((fn) => fn());
  }, []);
  return rootRef;
}

export const LUX_CSS = `
.fx-root { text-align:left; background:#fff; color:#131a26; overflow-x:hidden;
  --fx-ink:#131a26; --fx-body:#4b5563; --fx-mid:#6b7280; --fx-faint:#9ca3af; --fx-line:#e5e7eb;
  --fx-soft:#f4f6f9; --fx-fire:#F93400; --fx-fire-dark:#D93800; --fx-blue:#0056B3; --fx-blue-deep:#003D82;
  font-family:'Poppins',sans-serif; }
.fx-root ::selection { background:var(--fx-fire); color:#fff; }

/* ---------- HERO ---------- */
.fx-hero { position:relative; min-height:72vh; display:flex; align-items:center; justify-content:center; text-align:center;
  padding:150px 24px 110px; overflow:hidden; background:#f4f6f9; --mx:0; --my:0; --sy:0; }
.fx-hero-video { position:absolute; left:-8%; top:-8%; width:116%; height:116%; max-width:none; object-fit:cover; opacity:.95;
  transform:translate(calc(var(--mx)*-8px), calc(var(--my)*-8px)); pointer-events:none; }
.fx-hero-scrim { position:absolute; inset:0; pointer-events:none;
  background:radial-gradient(62% 66% at 50% 48%, rgba(255,255,255,.86), rgba(255,255,255,.46) 58%, transparent 84%),
             linear-gradient(180deg, rgba(255,255,255,.5), transparent 26% 72%, rgba(255,255,255,.72)); }
.fx-hero-inner { position:relative; z-index:2; max-width:900px;
  transform:translate(calc(var(--mx)*6px), calc(var(--my)*6px)); }
.fx-eyebrow { display:inline-block; font-family:'Courier New',monospace; font-size:11px; letter-spacing:.26em; text-transform:uppercase;
  color:var(--fx-blue); background:rgba(0,86,179,.09); border:1px solid rgba(0,86,179,.2); padding:7px 16px; border-radius:999px; margin-bottom:24px; }
.fx-hero h1 { font-size:clamp(34px,5.6vw,66px); font-weight:800; letter-spacing:-.02em; line-height:1.1; color:var(--fx-ink);
  margin:0; text-shadow:0 2px 22px rgba(255,255,255,.8); }
.fx-hero h1 .fx-accent { color:var(--fx-fire); }
/* home-consistent hero variants */
.fx-hero.fx-tall { min-height:88vh; }
.fx-hero.fx-mono h1 { font-family:'Courier New',ui-monospace,Consolas,monospace; font-weight:700; letter-spacing:-.01em; line-height:1.14; }
.fx-hero .fx-hero-cta { margin-top:34px; display:flex; gap:14px; justify-content:center; flex-wrap:wrap; }
/* parallax depth orbs (driven by --mx/--my from useLuxMotion) */
.fx-orb { position:absolute; border-radius:50%; filter:blur(60px); opacity:.4; pointer-events:none; z-index:1; }
.fx-orb-a { width:360px; height:360px; left:5%; top:22%; background:radial-gradient(circle, rgba(249,52,0,.5), transparent 70%);
  transform:translate(calc(var(--mx,0)*40px), calc(var(--my,0)*40px)); }
.fx-orb-b { width:320px; height:320px; right:7%; top:30%; background:radial-gradient(circle, rgba(0,86,179,.45), transparent 70%);
  transform:translate(calc(var(--mx,0)*-52px), calc(var(--my,0)*-30px)); }

/* bold statement text section (below hero) */
.fx-story { padding:100px 24px; background:#fff; text-align:center; }
.fx-story .fx-story-inner { max-width:900px; margin:0 auto; }
.fx-story .fx-story-eyebrow { font-family:'Courier New',monospace; font-size:12px; letter-spacing:.28em; text-transform:uppercase;
  color:var(--fx-fire); font-weight:700; display:inline-block; margin-bottom:22px; }
.fx-story p { font-size:clamp(21px,2.9vw,34px); font-weight:600; line-height:1.42; letter-spacing:-.01em; color:var(--fx-ink); }
.fx-story p .fx-accent { color:var(--fx-fire); }
.fx-story p .fx-dim { color:var(--fx-mid); }
.fx-hero p { color:#2a3340; font-size:clamp(15px,1.6vw,19px); max-width:640px; margin:22px auto 0; line-height:1.65; font-weight:500;
  text-shadow:0 1px 14px rgba(255,255,255,.85); }

/* ---------- shared bits ---------- */
.fx-reveal { opacity:0; transform:translateY(30px); transition:opacity .8s ease, transform .8s cubic-bezier(.16,.84,.32,1); }
.fx-reveal.on { opacity:1; transform:none; }
.fx-pop { opacity:0; transform:translateY(20px) scale(.95); transition:opacity .7s ease, transform .7s cubic-bezier(.34,1.56,.64,1); }
.fx-pop.on { opacity:1; transform:none; }
.fx-section { padding:100px 24px; }
.fx-section.fx-soft { background:var(--fx-soft); }
.fx-wrap { max-width:1180px; margin:0 auto; }
.fx-head { text-align:center; max-width:760px; margin:0 auto 56px; }
.fx-head h2 { font-size:clamp(28px,4vw,46px); font-weight:800; letter-spacing:-.015em; color:var(--fx-ink); line-height:1.2; }
.fx-head h2 .fx-accent { color:var(--fx-fire); }
.fx-head p { color:var(--fx-body); font-size:16px; line-height:1.7; margin-top:16px; }
.fx-btn { display:inline-flex; align-items:center; gap:10px; background:var(--fx-fire); color:#fff; text-decoration:none; font-weight:600;
  font-size:15px; padding:15px 30px; border-radius:999px; border:1px solid var(--fx-fire); box-shadow:0 12px 34px rgba(249,52,0,.32);
  transition:transform .25s cubic-bezier(.34,1.56,.64,1), background .2s, box-shadow .25s; }
.fx-btn:hover { background:var(--fx-fire-dark); border-color:var(--fx-fire-dark); transform:translateY(-3px) scale(1.03); color:#fff; }
.fx-btn.fx-ghost { background:#fff; color:var(--fx-ink); border-color:var(--fx-line); box-shadow:0 10px 30px rgba(19,26,38,.1); }
.fx-btn.fx-ghost:hover { background:#fff; color:var(--fx-fire); border-color:var(--fx-fire); }
.fx-btn .fx-arr { transition:transform .25s; } .fx-btn:hover .fx-arr { transform:translateX(5px); }

/* ---------- pillar cards ---------- */
.fx-grid { display:grid; gap:26px; }
.fx-grid-3 { grid-template-columns:repeat(3,1fr); }
.fx-grid-2 { grid-template-columns:repeat(2,1fr); }
.fx-card { position:relative; background:#fff; border:1px solid var(--fx-line); border-radius:22px; overflow:hidden;
  box-shadow:0 20px 50px -28px rgba(19,26,38,.3); transition:transform .35s cubic-bezier(.16,.84,.32,1), box-shadow .35s; }
.fx-card:hover { box-shadow:0 30px 70px -28px rgba(19,26,38,.4); }
.fx-card-media { position:relative; height:172px; overflow:hidden; background:#eef2f7; }
.fx-card-media img { width:100%; height:100%; object-fit:cover; display:block; transition:transform .6s cubic-bezier(.16,.84,.32,1); }
.fx-card:hover .fx-card-media img { transform:scale(1.06); }
.fx-card-media::after { content:''; position:absolute; inset:0; background:linear-gradient(180deg, transparent 40%, rgba(255,255,255,.9)); }
.fx-card-body { padding:26px 28px 30px; }
.fx-card-num { font-family:'Courier New',monospace; font-size:11px; letter-spacing:.22em; color:var(--fx-faint); }
.fx-card h3 { font-size:22px; font-weight:800; color:var(--fx-ink); margin:8px 0 12px; letter-spacing:-.01em; }
.fx-card p { color:var(--fx-body); font-size:14.5px; line-height:1.65; }
.fx-card ul { list-style:none; padding:0; margin:18px 0 0; }
.fx-card li { position:relative; padding:9px 0 9px 22px; font-size:14px; color:var(--fx-body); border-top:1px solid var(--fx-line); }
.fx-card li::before { content:''; position:absolute; left:0; top:16px; width:7px; height:7px; border-radius:2px; background:var(--fx-blue); }
.fx-card li a { color:inherit; text-decoration:none; transition:color .2s; }
.fx-card li:hover a { color:var(--fx-fire); }
.fx-card-accent { position:absolute; top:0; left:0; right:0; height:4px; background:linear-gradient(90deg,var(--fx-fire),var(--fx-blue)); z-index:2; }
.fx-card-link { display:block; text-decoration:none; }
.fx-card-go { display:inline-flex; align-items:center; gap:8px; margin-top:20px; font-family:'Courier New',monospace; font-size:12px;
  letter-spacing:.14em; text-transform:uppercase; color:var(--fx-fire); font-weight:700; }
.fx-card-go .fx-arr { transition:transform .25s; } .fx-card-link:hover .fx-card-go .fx-arr { transform:translateX(5px); }

/* ---------- stat strip ---------- */
.fx-stats { display:grid; grid-template-columns:repeat(4,1fr); gap:20px; }
.fx-stat { text-align:center; padding:28px 16px; background:#fff; border:1px solid var(--fx-line); border-radius:18px; }
.fx-stat b { display:block; font-size:clamp(26px,3.4vw,40px); font-weight:800; color:var(--fx-fire); letter-spacing:-.02em; }
.fx-stat span { display:block; margin-top:8px; font-size:13px; color:var(--fx-mid); letter-spacing:.04em; }

/* ---------- CTA band ---------- */
.fx-cta { position:relative; padding:110px 24px; text-align:center; overflow:hidden; background:var(--fx-soft); }
.fx-cta-video { position:absolute; left:-6%; top:-6%; width:112%; height:112%; max-width:none; object-fit:cover; opacity:.92; }
.fx-cta-scrim { position:absolute; inset:0; pointer-events:none;
  background:radial-gradient(64% 62% at 50% 50%, rgba(255,255,255,.86), rgba(255,255,255,.45) 58%, transparent 84%),
             linear-gradient(180deg, rgba(255,255,255,.55), transparent 24% 74%, rgba(255,255,255,.7)); }
.fx-cta-inner { position:relative; z-index:2; max-width:760px; margin:0 auto; }
.fx-cta h2 { font-size:clamp(30px,4.8vw,54px); font-weight:800; color:var(--fx-ink); letter-spacing:-.02em; margin-bottom:16px;
  text-shadow:0 2px 20px rgba(255,255,255,.85); }
.fx-cta p { display:table; margin:0 auto 30px; color:#2a3340; font-size:17px; font-weight:500;
  background:rgba(255,255,255,.78); backdrop-filter:blur(6px); -webkit-backdrop-filter:blur(6px);
  padding:10px 22px; border-radius:999px; border:1px solid rgba(255,255,255,.9); box-shadow:0 6px 24px rgba(19,26,38,.06); }

/* ---------- lead + points (module pages) ---------- */
.fx-lead { max-width:820px; margin:0 auto 8px; text-align:center; }
.fx-lead h2 { font-size:clamp(26px,3.6vw,42px); font-weight:800; letter-spacing:-.015em; color:var(--fx-ink); line-height:1.22; }
.fx-lead h2 .fx-accent { color:var(--fx-fire); }
.fx-lead p { color:var(--fx-body); font-size:17px; line-height:1.7; margin-top:16px; }
.fx-points { max-width:900px; margin:46px auto 0; display:grid; grid-template-columns:repeat(2,1fr); gap:18px; }
.fx-point { position:relative; background:#fff; border:1px solid var(--fx-line); border-radius:16px; padding:24px 26px 24px 56px;
  box-shadow:0 16px 40px -30px rgba(19,26,38,.4); transition:transform .3s, box-shadow .3s, border-color .3s; }
.fx-point:hover { transform:translateY(-4px); border-color:rgba(0,86,179,.35); box-shadow:0 24px 50px -30px rgba(19,26,38,.45); }
.fx-point::before { content:''; position:absolute; left:24px; top:29px; width:12px; height:12px; border-radius:3px;
  background:linear-gradient(135deg,var(--fx-fire),var(--fx-blue)); }
.fx-point h4 { font-size:17px; font-weight:800; color:var(--fx-ink); margin-bottom:6px; letter-spacing:-.01em; }
.fx-point p { font-size:14px; color:var(--fx-body); line-height:1.6; }

/* ---------- video-led statement band ---------- */
.fx-band { position:relative; padding:130px 24px; text-align:center; overflow:hidden; background:var(--fx-soft); }
.fx-band video { position:absolute; left:-6%; top:-6%; width:112%; height:112%; max-width:none; object-fit:cover; opacity:.92; }
.fx-band .fx-band-scrim { position:absolute; inset:0; pointer-events:none;
  background:radial-gradient(64% 62% at 50% 50%, rgba(255,255,255,.84), rgba(255,255,255,.44) 58%, transparent 84%),
             linear-gradient(180deg, rgba(255,255,255,.55), transparent 24% 74%, rgba(255,255,255,.72)); }
.fx-band .fx-band-inner { position:relative; z-index:2; max-width:840px; margin:0 auto; }
.fx-band h2 { font-size:clamp(28px,4.6vw,52px); font-weight:800; color:var(--fx-ink); letter-spacing:-.02em; line-height:1.16;
  text-shadow:0 2px 20px rgba(255,255,255,.85); }
.fx-band h2 .fx-accent { color:var(--fx-fire); }
.fx-band p { display:table; margin:20px auto 0; color:#2a3340; font-size:clamp(15px,1.6vw,18px); font-weight:500;
  background:rgba(255,255,255,.78); backdrop-filter:blur(6px); -webkit-backdrop-filter:blur(6px);
  padding:10px 22px; border-radius:999px; border:1px solid rgba(255,255,255,.9); box-shadow:0 6px 24px rgba(19,26,38,.06); }

/* ---------- How X Works — vertical dropdowns + 0→100 scroll rail ---------- */
.fx-method { max-width:940px; margin:44px auto 0; display:grid; grid-template-columns:76px 1fr; gap:34px; align-items:stretch; }
.fx-rail { position:relative; }
.fx-rail-track { position:relative; height:100%; width:4px; background:var(--fx-line); border-radius:4px; margin:0 auto; }
.fx-rail-fill { position:absolute; left:0; top:0; width:100%; height:100%; transform-origin:top; transform:scaleY(0);
  background:linear-gradient(180deg,var(--fx-fire),var(--fx-blue)); border-radius:4px; }
.fx-rail-pct { position:absolute; left:50%; top:0; transform:translate(-50%,-50%); width:44px; height:44px; border-radius:50%;
  display:flex; align-items:center; justify-content:center; font-family:'Courier New',monospace; font-size:13px; font-weight:800;
  color:#fff; background:linear-gradient(135deg,var(--fx-fire),var(--fx-blue)); box-shadow:0 10px 24px -8px rgba(249,52,0,.65);
  border:2px solid #fff; }
.fx-msteps { }
.fx-mstep { border-top:1px solid var(--fx-line); }
.fx-mstep:last-child { border-bottom:1px solid var(--fx-line); }
.fx-mstep-head { display:flex; align-items:center; gap:18px; padding:24px 6px; cursor:pointer; user-select:none; }
.fx-mstep-n { font-family:'Courier New',monospace; font-size:13px; font-weight:800; color:var(--fx-fire); min-width:34px; }
.fx-mstep-head h4 { flex:1; font-size:clamp(18px,2.1vw,25px); font-weight:800; color:var(--fx-ink); letter-spacing:-.01em; }
.fx-mstep-plus { font-family:'Courier New',monospace; font-size:22px; color:var(--fx-faint); transition:transform .35s, color .3s; flex-shrink:0; }
.fx-mstep.open .fx-mstep-plus { transform:rotate(45deg); color:var(--fx-fire); }
.fx-mstep-head:hover h4 { color:var(--fx-fire); }
.fx-mstep-body { max-height:0; overflow:hidden; transition:max-height .5s cubic-bezier(.4,0,.2,1); }
.fx-mstep-body p { padding:0 6px 26px 52px; color:var(--fx-body); line-height:1.66; font-size:15px; max-width:640px; }
@media (max-width:760px) { .fx-method { grid-template-columns:44px 1fr; gap:16px; } .fx-mstep-body p { padding-left:6px; } .fx-rail-pct { width:38px; height:38px; font-size:12px; } }

/* ---------- checklist (Who X Is For) ---------- */
.fx-checklist { max-width:840px; margin:44px auto 0; display:flex; flex-direction:column; gap:14px; }
.fx-check { position:relative; background:#fff; border:1px solid var(--fx-line); border-radius:14px; padding:18px 22px 18px 56px;
  font-size:15.5px; color:var(--fx-body); line-height:1.55; box-shadow:0 14px 36px -30px rgba(19,26,38,.4);
  transition:transform .3s, border-color .3s, box-shadow .3s; }
.fx-check:hover { transform:translateX(4px); border-color:rgba(0,86,179,.35); box-shadow:0 20px 44px -30px rgba(19,26,38,.45); }
.fx-check::before { content:'✓'; position:absolute; left:20px; top:50%; transform:translateY(-50%); width:22px; height:22px;
  border-radius:6px; background:linear-gradient(135deg,var(--fx-fire),var(--fx-blue)); color:#fff; font-size:12px; font-weight:800;
  display:flex; align-items:center; justify-content:center; }

/* ---------- comparison table ---------- */
.fx-compare { max-width:960px; margin:46px auto 0; border:1px solid var(--fx-line); border-radius:18px; overflow:hidden;
  box-shadow:0 24px 60px -40px rgba(19,26,38,.45); }
.fx-compare table { width:100%; border-collapse:collapse; }
.fx-compare th { background:#131a26; color:#fff; font-weight:700; padding:16px 20px; text-align:left; font-size:14px; letter-spacing:.01em; }
.fx-compare td { padding:16px 20px; border-top:1px solid var(--fx-line); font-size:14.5px; color:var(--fx-body); line-height:1.5; background:#fff; }
.fx-compare tr:nth-child(even) td { background:#fafbfc; }
.fx-compare .fx-col-win { position:relative; }
.fx-compare th.fx-col-win { background:var(--fx-fire); }
.fx-compare td.fx-col-win { color:var(--fx-ink); font-weight:600; background:rgba(249,52,0,.05) !important; }

/* ---------- global hubs ---------- */
.fx-hubs { display:flex; justify-content:center; gap:14px; flex-wrap:wrap; margin-top:30px; }
.fx-hub { font-family:'Courier New',monospace; font-size:12px; letter-spacing:.14em; text-transform:uppercase; color:var(--fx-blue);
  background:rgba(0,86,179,.07); border:1px solid rgba(0,86,179,.2); border-radius:999px; padding:11px 22px; }

/* ---------- FAQ ---------- */
.fx-faq { max-width:840px; margin:44px auto 0; }
.fx-faq-item { border-top:1px solid var(--fx-line); }
.fx-faq-item:last-child { border-bottom:1px solid var(--fx-line); }
.fx-faq-q { display:flex; justify-content:space-between; align-items:center; gap:16px; padding:22px 4px; cursor:pointer;
  font-size:clamp(16px,1.7vw,18px); font-weight:700; color:var(--fx-ink); }
.fx-faq-q .fx-faq-plus { font-family:'Courier New',monospace; font-size:20px; color:var(--fx-faint); transition:transform .35s; flex-shrink:0; }
.fx-faq-item.faq-open .fx-faq-q .fx-faq-plus { transform:rotate(45deg); color:var(--fx-fire); }
.fx-faq-a { max-height:0; overflow:hidden; transition:max-height .5s cubic-bezier(.4,0,.2,1); }
.fx-faq-a p { padding:0 4px 24px; color:var(--fx-body); line-height:1.68; font-size:15px; max-width:720px; }

@media (max-width:980px) { .fx-grid-3 { grid-template-columns:1fr; } .fx-grid-2 { grid-template-columns:1fr; } .fx-stats { grid-template-columns:repeat(2,1fr); } .fx-points { grid-template-columns:1fr; } .fx-steps { grid-template-columns:1fr; }
  .fx-compare { overflow-x:auto; } .fx-compare table { min-width:520px; } }
@media (prefers-reduced-motion: reduce) {
  .fx-reveal, .fx-pop { opacity:1 !important; transform:none !important; }
  .fx-hero-video, .fx-cta-video { display:none; }
}
`;
